import { memo, useRef, useState, type ComponentPropsWithoutRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';

// Markdown — безопасный (sanitize) рендер пользовательского markdown с GFM.
// Используется для body_md уроков, description_md курсов/программ/заданий
// и текстов сабмишенов/уведомлений. Никогда не рендерим raw HTML.
export type MarkdownProps = {
  content: string | null | undefined;
  className?: string;
  empty?: string;
};

// CodeBlock — обёртка над <pre> с кнопкой копирования в буфер обмена.
// Нужна для практических материалов (промпты), которые студент копирует целиком.
function CodeBlock({ children, ...props }: ComponentPropsWithoutRef<'pre'>) {
  const ref = useRef<HTMLPreElement>(null);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    const text = ref.current?.innerText ?? '';
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard может быть недоступен (insecure context) — молча игнорируем */
    }
  };

  return (
    <div className="group relative">
      <button
        type="button"
        onClick={copy}
        className="absolute right-2 top-2 z-10 rounded-md bg-neutral-700/80 px-2 py-1 text-xs font-medium text-neutral-100 opacity-0 transition hover:bg-neutral-600 focus:opacity-100 group-hover:opacity-100"
        aria-label="Скопировать в буфер обмена"
        data-testid="md-copy-button"
      >
        {copied ? 'Скопировано' : 'Копировать'}
      </button>
      <pre ref={ref} {...props}>
        {children}
      </pre>
    </div>
  );
}

function MarkdownImpl({ content, className, empty }: MarkdownProps) {
  const text = content?.trim();
  if (!text) return empty ? <p className="text-sm text-neutral-500">{empty}</p> : null;
  return (
    <div
      className={
        className ??
        'prose prose-sm max-w-none prose-headings:scroll-mt-20 prose-pre:bg-neutral-900 prose-pre:text-neutral-100'
      }
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={{ pre: CodeBlock }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

export const Markdown = memo(MarkdownImpl);
